import type { FunctionComponent } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { useUserIdPageTargeting } from "~/components/advertisement/google-publisher-tag";
import { useAdvertisementViewabilitySetup } from "~/components/advertisement/viewability-tracking";
import { useAppPromotionSnackbar } from "~/components/common/snackbar/hooks";
import { useContentBookmarkActionRedirect } from "~/components/content/content-bookmark/content-bookmark-action-redirect/hooks";
import { useFollowActionRedirect } from "~/components/follow-button/follow-action-redirect/hooks";
import { useFollowConfigActionRedirect } from "~/components/follow-button/follow-config-action-redirect/hooks";
import { useBookmarkRippleEffectSetup } from "~/components/header/header-bookmark/hooks";
import { useTrackingSetup } from "~/components/tracking";
import { useGDPRSetup } from "~/components/tracking/gdpr/hooks";
import {
  useOnePlusXPageTargeting,
  useOnePlusXSetup,
  useTrackPageLoad,
  useTrackRouteChange,
} from "~/components/tracking/one-plus-x";
import { useRefreshToken } from "~/lib/account";
import { useAccountSDKSetup } from "~/lib/account/hooks";
import { useAppSetup as useAppSetup_ } from "~/lib/app/hooks";
import { useSentrySetup, useSyncV1Data } from "~/lib/hooks";
import { useRosettaSetup } from "~/lib/rosetta";
import { useRosettaPianoTemplateTracking } from "~/lib/rosetta/hooks/rosetta-piano-template-tracking";

import { ErrorHandler } from "./error-handler";
import { HookRunner } from "./hook-runner";

/**
 * This component is using for running the hooks that no need to be prioritized
 */
export const SimpleHooksRunner: FunctionComponent = () => {
  const useAppSetup = () => {
    useAppSetup_();
  };

  return (
    <>
      {[
        useSentrySetup,
        useSyncV1Data,
        useAdvertisementViewabilitySetup,
        useAppSetup,
        useBookmarkRippleEffectSetup,
        useGDPRSetup,
        useOnePlusXSetup,
        useRosettaSetup,
        useRosettaPianoTemplateTracking,
        useTrackingSetup,
        useUserIdPageTargeting,
        useOnePlusXPageTargeting,
        useTrackPageLoad,
        useTrackRouteChange,
        useContentBookmarkActionRedirect,
        useFollowActionRedirect,
        useFollowConfigActionRedirect,
        useAccountSDKSetup,
        useRefreshToken,
        useAppPromotionSnackbar,
      ].map((hook, index) => (
        <ErrorBoundary FallbackComponent={ErrorHandler} key={index}>
          {<HookRunner hook={hook} />}
        </ErrorBoundary>
      ))}
    </>
  );
};
SimpleHooksRunner.displayName = "SimpleHooksRunner";
